import React, { useState, useRef, useEffect } from 'react';

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  showPassword?: boolean;
}

export const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  placeholder = '',
  className = '',
  disabled = false,
  required = false,
  showPassword = false
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showPassword) {
      setDisplayValue(value);
    } else {
      // Create asterisks for each character
      setDisplayValue('*'.repeat(value.length));
    }
  }, [value, showPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const currentValue = value;
    
    if (showPassword) {
      onChange(newValue);
      return;
    }

    // Handle input when password is hidden
    if (newValue.length > currentValue.length) {
      // Character was added
      const addedChar = newValue[newValue.length - 1];
      if (addedChar !== '*') {
        onChange(currentValue + addedChar);
      }
    } else if (newValue.length < displayValue.length) {
      // Character was deleted
      onChange(currentValue.slice(0, newValue.length));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showPassword) return;

    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
    // Handle delete
    else if (e.key === 'Delete') {
      e.preventDefault();
      const cursorPosition = inputRef.current?.selectionStart || 0;
      const newValue = value.slice(0, cursorPosition) + value.slice(cursorPosition + 1);
      onChange(newValue);
    }
    // Handle regular characters
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      onChange(value + e.key);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (showPassword) return;
    
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    onChange(value + pastedText);
  };

  return (
    <input
      ref={inputRef}
      type={showPassword ? 'text' : 'text'} // Always use text type for custom handling
      value={displayValue}
      onChange={handleInputChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
      autoComplete="current-password"
    />
  );
};
